import { useEffect, useMemo, useRef, useState } from 'react';
import { DateRangeState, HistoricalReportSettings } from '../types/HistoricalReport';
import { DateTimeRange, NestedItem } from '@perry-weather/component-library';
import { useReportFileName } from './useReportFileName';
import {
  useHardwareQuery,
  useOrganizationLocationsQuery,
  User,
  useSitesQuery,
  useUserQuery,
  useWxDataQuery,
} from '../Services/API';
import { User as AuthUser } from 'oidc-react';
import mixpanel from 'mixpanel-browser';
import { TrackableEvents } from '../Components/Tracked/events';
import { useGenerateReportMutation } from '../Services/API/reportsApi';
import { useAppDispatch } from '../app/hooks';
import { openToast } from '../features/toast/toastSlice';
import { useReportValidation } from './useReportValidation';
import {
  OBSERVATIONS_DATA_INTERVAL,
  OBSERVATIONS_DATA_INTERVAL_ARRAY,
  SUMAMARY_FREQUENCY,
  FORMATS,
  DEFAULT_MAX_CHIPS,
  OBSERVATIONS,
  POLICIES,
  POLICY_DATA_INTERVAL_ARRAY,
  ReportType,
} from '../Constants/HistoricalReportConstants';

interface UseHistoricalReportStateProps {
  open: boolean;
  hardwareId: number;
  users: User[] | undefined;
  userAuthData: AuthUser | null | undefined;
  organizationLocationId?: string | null;
  weatherStationId?: string;
  hasMesonetAccess: boolean;
}

const TRACK_PROPERTIES = {
  button_location: 'Historical Report',
  button_feature: 'Historical Export',
  button_type: 'button',
};

export const useHistoricalReportForm = ({
  open,
  hardwareId,
  users,
  userAuthData,
  organizationLocationId,
  weatherStationId,
  hasMesonetAccess,
}: UseHistoricalReportStateProps) => {
  const dispatch = useAppDispatch();
  const { data: sites } = useSitesQuery();
  const { data: userData } = useUserQuery();
  const { data: hardwareList } = useHardwareQuery(undefined);
  const { data: orgLocations } = useOrganizationLocationsQuery();
  const [generateReport, { isLoading: isReportLoading, error: reportError }] = useGenerateReportMutation();
  const { data: openStation } = useWxDataQuery(weatherStationId ?? '', {
    skip: weatherStationId === undefined,
    pollingInterval: 60000,
  });

  const [dateRangeState, setDateRangeState] = useState<DateRangeState>({
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
  });

  const initialSettings = useRef<HistoricalReportSettings>({
    // origin: ORIGINS[0],
    addedUsers: [],
    weatherStations: [hardwareId],
    locations: organizationLocationId ? [organizationLocationId] : [],
    granularity: OBSERVATIONS_DATA_INTERVAL[OBSERVATIONS_DATA_INTERVAL_ARRAY[3][0]],
    includeRecipients: false,
    frequency: SUMAMARY_FREQUENCY[0],
    formats: FORMATS.reduce((acc: Record<string, boolean>, name: string) => {
      acc[name] = false;
      return acc;
    }, {}),
    isRecurring: false,
    recurringPeriod: 'MONTH',
    recurringMonthDay: '1',
    recurringWeekDay: 'MONDAY',
    // recurringHour:'1';
    // Weather conditions
    weatherObservations: new Map<string, string[]>(),
    observationsDataInterval: OBSERVATIONS_DATA_INTERVAL_ARRAY[3][0],
    //Policy delays
    policies: new Map<string, string[]>(),
    policiesDataInterval: POLICY_DATA_INTERVAL_ARRAY[3][0],
  });

  const [settings, setSettings] = useState<HistoricalReportSettings>(initialSettings.current);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [userIdEmail, setUserIdEmail] = useState<Map<string, string | null>>(new Map<string, string | null>());
  const [groupEmails, setGroupEmails] = useState<NestedItem[]>([]);
  const [fetchedStations, setFetchedStations] = useState<{ value: string; label: string; webRelayType?: number }[]>([]);
  const [fetchedLocations, setFetchedLocations] = useState<{ value: string; label: string }[]>([]);
  const [selectedView, setSelectedView] = useState(ReportType.WeatherConditions);

  const [observationSwitchAllTrigger, setObservationSwitchAllTrigger] = useState([false, false]);
  const [policySwitchAllTrigger, setPolicySwitchAllTrigger] = useState([false, false]);
  const [maxChips, setMaxChips] = useState(DEFAULT_MAX_CHIPS);

  const fetchedStationsMap = useMemo(() => {
    return new Map<string, string>(fetchedStations.map(({ value, label }) => [value, label]));
  }, [fetchedStations]);

  const fetchedLocationsMap = useMemo(() => {
    return new Map<string, string>(fetchedLocations.map(({ value, label }) => [value, label]));
  }, [fetchedLocations]);

  const allowedObservations = OBSERVATIONS.filter(
    obs => OBSERVATIONS_DATA_INTERVAL[obs.minInterval] <= settings.granularity
  );
  const observartionsMap = new Map<string, string>(
    allowedObservations.map(({ value, minInterval }) => [value, minInterval])
  );

  const { fileNameState, getReportName, buildReportName, handleChangeFileName, updateFileNameState } =
    useReportFileName();

  const { validations, isExportEnabled, showPrecipError, validateView, validateDateRange, updateValidation } =
    useReportValidation({
      settings,
      hardwareList,
      dateRangeState,
      view: selectedView,
      selectedGranularity: settings.granularity,
      selectedStations: settings.weatherStations,
      selectedLocations: settings.locations,
      fileName: fileNameState.name,
    });

  useEffect(() => {
    initialSettings.current = { ...initialSettings.current, weatherStations: [hardwareId] };
  }, [hardwareId]);

  useEffect(() => {
    if (!open) return;
    setDateRangeState({
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
      daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    });
    setSettings(initialSettings.current);
    setObservationSwitchAllTrigger([false, false]);
    setPolicySwitchAllTrigger([false, false]);
    updateValidation({ isDateRangeValid: true });
    updateFileNameState({
      name: buildReportName(ReportType.WeatherConditions, fetchedStationsMap, [hardwareId], null, null),
      isEditable: true,
    });
  }, [open, hardwareId, initialSettings.current]);

  useEffect(() => {
    if (!open) return;

    const hasChanges =
      settings.weatherObservations.size > 0 ||
      settings.addedUsers.length > 0 ||
      dateRangeState.startDate !== null ||
      dateRangeState.endDate !== null ||
      settings.weatherStations.length > 1;

    setHasUnsavedChanges(hasChanges);
  }, [settings, dateRangeState, open, settings.weatherStations]);

  useMemo(() => {
    const filteredStations =
      hardwareList
        ?.filter(x => x.modelId !== 'OWS')
        .map(x => {
          return { value: x.id.toString(), label: x.name, webRelayType: x.webRelayType };
        }) ?? [];

    // append opened station to stations fetched if Mesonet access
    if (hasMesonetAccess && openStation) {
      const openStationHardwareId = openStation.hardwareId;
      if (openStationHardwareId) {
        initialSettings.current = { ...initialSettings.current, weatherStations: [openStationHardwareId] };
        filteredStations?.push({
          value: openStationHardwareId.toString(),
          label: openStation.locationName,
          webRelayType: 0,
        });
      }
    }

    if (filteredStations?.length) setFetchedStations(filteredStations);
  }, [hardwareList, hasMesonetAccess, openStation]);

  useMemo(() => {
    const filteredLocations = orgLocations?.map(x => {
      return { value: x.id, label: x.name };
    });
    if (filteredLocations) setFetchedLocations(filteredLocations);
  }, [orgLocations]);

  useMemo(() => {
    if (!users) return;

    const newuserIdMails = new Map<string, string | null>();
    users.forEach(x => {
      newuserIdMails.set(x.id, x.email);
    });

    setUserIdEmail(newuserIdMails);
  }, [users]);

  useMemo(() => {
    if (userAuthData && userAuthData.profile && users && sites) {
      let options: any[] = [];
      if (userAuthData.profile.Role === 'Assistant Admin') {
        let currentUser = users.find(x => x.id === userAuthData.profile.sub);
        if (currentUser) {
          options = sites
            .filter(site => site.id === currentUser!.siteId)
            .map(site => ({
              label: site.name,
              siteId: site.id,
              children: users
                .filter(
                  user =>
                    user.id !== userAuthData?.profile?.sub &&
                    user.siteId === currentUser!.siteId &&
                    user.siteId === site.id
                )
                .map(user => ({ value: user.id, label: user.email })),
            }));
        }
      } else {
        options = sites
          .filter(site => users.some(u => u.siteId === site.id))
          .map(site => ({
            label: site.name,
            siteId: site.id,
            children: users
              .filter(user => user.id !== userAuthData.profile.sub && user.isActive && user.siteId === site.id)
              .map(user => ({ value: user.id, label: user.email })),
          }));
      }

      setGroupEmails(options);
    }
  }, [users, sites, userAuthData]);

  const getCurrentNameMap = () => {
    switch (selectedView) {
      case ReportType.WeatherConditions:
        return fetchedStationsMap;
      case ReportType.PolicyDelays:
        return fetchedLocationsMap;
      default:
        return new Map();
    }
  };

  const handleDateRangeChange = (newDateRange: DateTimeRange) => {
    const startDate = newDateRange.dateRange.start;
    const endDate = newDateRange.dateRange.end;

    if (
      !dateRangeState.startDate?.isSame(newDateRange.dateRange.start) ||
      dateRangeState.endDate?.isSame(newDateRange.dateRange.end)
    ) {
      mixpanel.track(TrackableEvents.ButtonClicked, {
        ...TRACK_PROPERTIES,
        button_label: 'Select Date Range',
        date_range: newDateRange.definedRange,
      });
    }

    if (!newDateRange.timeRange)
      mixpanel.track(TrackableEvents.ButtonClicked, {
        ...TRACK_PROPERTIES,
        button_label: 'All Day',
      });

    if (dateRangeState.daysOfWeek.toString() !== newDateRange.days?.toString())
      mixpanel.track(TrackableEvents.ButtonClicked, {
        ...TRACK_PROPERTIES,
        button_label: 'Days',
      });

    setDateRangeState({
      startDate: startDate,
      endDate: endDate,
      startTime: newDateRange?.timeRange?.start ?? null,
      endTime: newDateRange?.timeRange?.end ?? null,
      daysOfWeek: newDateRange?.days ?? [],
    });

    validateDateRange(settings.observationsDataInterval, startDate, endDate);

    switch (selectedView) {
      case ReportType.WeatherConditions:
        updateFileNameState({
          name: getReportName(
            ReportType.WeatherConditions,
            getCurrentNameMap(),
            settings.weatherStations,
            startDate,
            endDate
          ),
        });
        break;
      case ReportType.PolicyDelays:
        updateFileNameState({
          name: getReportName(ReportType.PolicyDelays, getCurrentNameMap(), settings.locations, startDate, endDate),
        });
        break;
      default:
        updateFileNameState({
          name: buildReportName(ReportType.WeatherConditions, new Map(), [hardwareId], null, null),
          isEditable: true,
        });
    }
  };

  const handleViewChange = (newView: ReportType) => {
    if (!newView) return;

    switch (newView) {
      case ReportType.WeatherConditions:
        updateFileNameState({
          name: getReportName(
            ReportType.WeatherConditions,
            fetchedStationsMap,
            settings.weatherStations,
            dateRangeState.startDate,
            dateRangeState.endDate
          ),
        });
        break;
      case ReportType.PolicyDelays:
        updateFileNameState({
          name: getReportName(
            ReportType.PolicyDelays,
            fetchedLocationsMap,
            settings.locations,
            dateRangeState.startDate,
            dateRangeState.endDate
          ),
        });
        break;
      default:
        updateFileNameState({
          name: buildReportName(ReportType.WeatherConditions, new Map(), [hardwareId], null, null),
          isEditable: true,
        });
    }

    setSelectedView(newView);
    validateView(newView, settings.observationsDataInterval, dateRangeState.startDate, dateRangeState.endDate);
  };

  const handleSelectedObservations = (observation: string, metrics: string[]) => {
    mixpanel.track(TrackableEvents.ButtonClicked, {
      ...TRACK_PROPERTIES,
      button_label: 'Select Observation',
      weather_observation: observation,
    });

    const newObservations = new Map(settings.weatherObservations);

    if (!metrics || metrics.length <= 0) {
      newObservations.delete(observation);
    } else newObservations.set(observation, metrics);

    setSettings(prevSettings => ({
      ...prevSettings,
      weatherObservations: newObservations,
    }));
  };

  const handleSelectedPolicies = (policy: string, types: string[]) => {
    mixpanel.track(TrackableEvents.ButtonClicked, {
      ...TRACK_PROPERTIES,
      button_label: 'Select Policy',
      weather_observation: policy,
    });

    const newPolicies = new Map(settings.policies);

    if (!types || types.length <= 0) {
      newPolicies.delete(policy);
    } else {
      newPolicies.set(policy, types);
    }

    setSettings(prevSettings => ({
      ...prevSettings,
      policies: newPolicies,
    }));
  };

  const handleRecipientsChange = (newUsers: string[]) => {
    mixpanel.track(TrackableEvents.ButtonClicked, {
      ...TRACK_PROPERTIES,
      button_label: 'Add Recipients',
    });

    setSettings(prevSettings => ({ ...prevSettings, addedUsers: newUsers }));
  };

  const handleObservationDataIntervalChange = (newDataInterval: string) => {
    mixpanel.track(TrackableEvents.ButtonClicked, {
      ...TRACK_PROPERTIES,
      button_label: 'Select Observations Data Interval',
      button_type: 'radio',
      data_interval: newDataInterval,
    });

    const currentObservations = new Map(settings.weatherObservations);

    settings.weatherObservations.forEach((_, observation) => {
      const minInterval = observartionsMap.get(observation);
      if (minInterval && OBSERVATIONS_DATA_INTERVAL[minInterval] > OBSERVATIONS_DATA_INTERVAL[newDataInterval])
        currentObservations.delete(observation);
    });

    setSettings(prevSettings => ({
      ...prevSettings,
      weatherObservations: currentObservations,
      observationsDataInterval: newDataInterval,
      granularity: OBSERVATIONS_DATA_INTERVAL[newDataInterval],
    }));

    validateDateRange(newDataInterval, dateRangeState.startDate, dateRangeState.endDate);
  };

  const handlePolicyDataIntervalChange = (newDataInterval: string) => {
    mixpanel.track(TrackableEvents.ButtonClicked, {
      ...TRACK_PROPERTIES,
      button_label: 'Select Policy Data Interval',
      button_type: 'radio',
      data_interval: newDataInterval,
    });

    setSettings(prevSettings => ({ ...prevSettings, policiesDataInterval: newDataInterval }));
    validateDateRange(newDataInterval, dateRangeState.startDate, dateRangeState.endDate);
  };

  const handleSelectAllObservations = () => {
    mixpanel.track(TrackableEvents.ButtonClicked, {
      ...TRACK_PROPERTIES,
      button_label: 'Select All Observations',
    });

    if (settings.weatherObservations.size >= allowedObservations.length) {
      setSettings({ ...settings, weatherObservations: new Map<string, string[]>() });
      setObservationSwitchAllTrigger([false, true]);
    } else {
      setSettings({
        ...settings,
        weatherObservations: new Map<string, string[]>(
          allowedObservations.map(({ value, metrics }) => [value, metrics.map(x => x.value)])
        ),
      });
      setObservationSwitchAllTrigger([true, false]);
    }

    setTimeout(() => {
      setObservationSwitchAllTrigger([false, false]);
    }, 200);
  };

  const handleSelectAllPolicies = () => {
    mixpanel.track(TrackableEvents.ButtonClicked, {
      ...TRACK_PROPERTIES,
      button_label: 'Select All Policies',
    });

    if (settings.policies.size >= POLICIES.length) {
      setSettings({ ...settings, policies: new Map<string, string[]>() });
      setPolicySwitchAllTrigger([false, true]);
    } else {
      const allPolicies = new Map<string, string[]>();

      POLICIES.forEach(policy => {
        if (!policy.types?.length) {
          allPolicies.set(policy.value, [policy.value]);
        } else {
          allPolicies.set(
            policy.value,
            policy.types.map(x => x.value)
          );
        }
      });
      // const policiesWithSubtypes = new Map<string, string[]>(
      //   POLICIES.filter(({ types }) => types?.length).map(({ value, types }) => [value, types!.map(x => x.value)])
      // );

      setSettings({
        ...settings,
        policies: allPolicies,
      });
      setPolicySwitchAllTrigger([true, false]);
    }

    setTimeout(() => {
      setPolicySwitchAllTrigger([false, false]);
    }, 200);
  };

  const handleUpdateWeatherStations = (stations: string[]) => {
    const stationsInt = stations.map(x => parseInt(x));
    setSettings({ ...settings, weatherStations: stationsInt });
    updateFileNameState({
      name: getReportName(
        ReportType.WeatherConditions,
        getCurrentNameMap(),
        stationsInt,
        dateRangeState.startDate,
        dateRangeState.endDate
      ),
    });

    if (fetchedStations.length === stations.length) {
      mixpanel.track(TrackableEvents.ButtonClicked, {
        ...TRACK_PROPERTIES,
        button_label: 'Select All Weather Stations',
        button_type: 'dropdown',
      });
    } else {
      mixpanel.track(TrackableEvents.ButtonClicked, {
        ...TRACK_PROPERTIES,
        button_label: 'Select Weather Station',
        button_type: 'dropdown',
      });
    }
  };

  const handleUpdateLocations = (locations: string[]) => {
    setSettings({ ...settings, locations: locations });
    updateFileNameState({
      name: getReportName(
        ReportType.PolicyDelays,
        getCurrentNameMap(),
        locations,
        dateRangeState.startDate,
        dateRangeState.endDate
      ),
    });

    if (fetchedLocations.length === locations.length) {
      mixpanel.track(TrackableEvents.ButtonClicked, {
        ...TRACK_PROPERTIES,
        button_label: 'Select All Oganization Locations',
        button_type: 'dropdown',
      });
    } else {
      mixpanel.track(TrackableEvents.ButtonClicked, {
        ...TRACK_PROPERTIES,
        button_label: 'Select Location',
        button_type: 'dropdown',
      });
    }
  };

  const handleSelectedFormats = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      formats: {
        ...prevSettings.formats,
        [event.target.name]: event.target.checked,
      },
    }));
  };

  const handleGenerateReport = async () => {
    mixpanel.track(TrackableEvents.ButtonClicked, {
      ...TRACK_PROPERTIES,
      button_label: 'Confirm Report Generation',
    });

    if (!dateRangeState.startDate || !dateRangeState.endDate) {
      return;
    }

    if (!userData || settings.weatherObservations.size <= 0) {
      return;
    }

    const observationAggregations = Object.fromEntries(settings.weatherObservations);

    const reportRequest = {
      Requestor: userData!.id,
      Name: fileNameState.name,
      Recipients: [userData!.id, ...settings.addedUsers],
      ReportTypes: [0],
      FileFormats: [0],
      UnitType: 0,
      DataInterval: OBSERVATIONS_DATA_INTERVAL[settings.observationsDataInterval],
      Filters: {
        HardwareIds: settings.weatherStations,
        ObservationTypes: observationAggregations,
        TimeFilter: {
          StartDate: dateRangeState.startDate?.format('YYYY-MM-DD') || '',
          EndDate: dateRangeState.endDate?.format('YYYY-MM-DD') || '',
          StartTimeOfDay: dateRangeState.startTime?.format('HH:mm:ss') || null,
          EndTimeOfDay: dateRangeState.endTime?.format('HH:mm:ss') || null,
          DaysOfWeek: dateRangeState.daysOfWeek,
        },
      },
    };

    try {
      generateReport(reportRequest);
    } catch (error) {
      console.error(error);
      dispatch(openToast({ variant: 'error', header: 'Error requesting data.' }));
    }

    return;
  };

  const updateSettings = (updates: Partial<HistoricalReportSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  const updateMaxChips = (update: number) => {
    setMaxChips(update);
  };

  return {
    settings,
    userIdEmail,
    groupEmails,
    dateRangeState,
    selectedView,
    hasUnsavedChanges,
    observationSwitchAllTrigger,
    policySwitchAllTrigger,
    weatherStations: fetchedStations,
    locations: fetchedLocations,
    fileName: fileNameState.name,
    allowedObservations,
    maxChips,
    updateSettings,
    updateMaxChips,
    // file name hook
    handleChangeFileName,
    // validation hook
    validations,
    isExportEnabled,
    showPrecipError,
    validateView,
    // handlers
    handleViewChange,
    handleDateRangeChange,
    handleSelectedObservations,
    handleSelectedPolicies,
    handleRecipientsChange,
    handleObservationDataIntervalChange,
    handlePolicyDataIntervalChange,
    handleSelectAllObservations,
    handleSelectAllPolicies,
    handleUpdateWeatherStations,
    handleUpdateLocations,
    handleSelectedFormats,
    handleGenerateReport,
  };
};
